import { handleAddToCollectionOp, invalidateCache } from "@sg/query";
import { useMutation, useQuery } from "react-query";
import { z } from "zod";
import { AuditableSchema, PermissionSchema, RoleV2 } from "~/types";
import { agent } from "~/util/agent";
import { QUERY_USERS_KEY } from "./user";

const QUERY_ROLES_KEY = "roles";

const RoleCreateSchema = z.object({
  name: z.string().trim().min(1),
  description: z.string().trim().min(1),
  permissions: PermissionSchema,
});

const RoleToggleSchema = z.object({
  member_id: z.string().uuid().min(1),
  role_id: z.string().uuid().min(1),
});

const RoleV2Schema = z.object({
  user_id: z.string().uuid().min(1),
  org_id: z.string().uuid().optional(),
  role: z.nativeEnum(RoleV2),
});

const RoleSchema = RoleCreateSchema.merge(AuditableSchema);

type RoleResourceType = z.infer<typeof RoleSchema>;
type CreateRoleInput = z.infer<typeof RoleCreateSchema>;
type ToggleRoleInput = z.infer<typeof RoleToggleSchema>;
type RoleInputV2 = z.infer<typeof RoleV2Schema>;

const useRoles = () => useQuery([QUERY_ROLES_KEY], async () => agent("roles/").json<RoleResourceType[]>());

const useRole = (id?: string) =>
  useQuery([QUERY_ROLES_KEY, id], async () => agent(`roles/${id}`).json<RoleResourceType>(), {
    enabled: !!id,
  });

const useAssignRole = () => {
  return useMutation<unknown, Error, ToggleRoleInput>(
    ({ role_id, member_id }) => {
      return agent.patch(`roles/${role_id}/assign`, { labels: ["updated", "role"], json: { member_id } });
    },
    {
      onSuccess(data, variables) {
        invalidateCache([QUERY_USERS_KEY], variables);
        invalidateCache([QUERY_USERS_KEY, variables.member_id], variables);
      },
    }
  );
};

const useUnassignRole = () => {
  return useMutation<RoleResourceType, Error, ToggleRoleInput>(
    ({ role_id, member_id }) => {
      return agent.patch(`roles/${role_id}/unassign`, { json: { member_id } }).json();
    },
    {
      onSuccess(data, variables) {
        invalidateCache([QUERY_USERS_KEY], variables);
        invalidateCache([QUERY_USERS_KEY, variables.member_id], variables);
      },
    }
  );
};

const useCreateRole = () => {
  return useMutation<unknown, Error, CreateRoleInput>(
    (data) => {
      return agent.post("roles/", { labels: ["created", "role"], json: data });
    },
    { ...handleAddToCollectionOp(QUERY_ROLES_KEY) }
  );
};

const useAddRoleV2 = () => {
  return useMutation<unknown, Error, RoleInputV2>(
    ({ user_id, org_id, role }) => {
      return agent.post(`users/${user_id}/add_role`, {
        labels: ["added", "role"],
        json: {
          org_id,
          role,
        },
      });
    },
    {
      onSuccess(data, variables) {
        invalidateCache([QUERY_USERS_KEY, variables.user_id], variables);
        invalidateCache([QUERY_USERS_KEY], variables);
      },
    }
  );
};

const useRemoveRoleV2 = () => {
  return useMutation<unknown, Error, RoleInputV2>(
    ({ user_id, org_id, role }) => {
      return agent.post(`users/${user_id}/remove_role`, {
        labels: ["removed", "role"],
        json: {
          org_id,
          role,
        },
      });
    },
    {
      onSuccess(data, variables) {
        invalidateCache([QUERY_USERS_KEY, variables.user_id], variables);
        invalidateCache([QUERY_USERS_KEY], variables);
      },
    }
  );
};

export {
  QUERY_ROLES_KEY,
  RoleCreateSchema,
  useAddRoleV2,
  useAssignRole,
  useCreateRole,
  useRemoveRoleV2,
  useRole,
  useRoles,
  useUnassignRole,
};
export type { CreateRoleInput, RoleResourceType };
